import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_newcompliance/artifacts/nocgcglt_CMS%20Logo.jpg" 
                alt="Compliance Management Systems" 
                className="h-12 w-auto bg-white rounded p-1"
              />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Your trusted partner for PECB-certified ISO training and professional development, serving the United States, Jamaica, the Caribbean, and global markets.
            </p>
            {/* PECB Authorized Partner Badge */}
            <div className="pt-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_training-comply/artifacts/a4vpv6m5_1-pecb-authorized-reseller-initiate.png" 
                alt="PECB Authorized Partner" 
                className="h-20 w-auto"
              />
            </div>
            <div className="flex space-x-4 pt-2">
              <a href="https://www.linkedin.com/company/cmsolutionsja/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-brand transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.facebook.com/cmsolutionsja/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-brand transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/cmsolutions_ja" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-brand transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-slate-400 hover:text-white transition-colors">
                  PECB Courses
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-slate-400 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-slate-400 hover:text-white transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Course Categories */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">Course Categories</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/courses?category=ISO 9001" className="text-sm text-slate-400 hover:text-white transition-colors">
                  ISO 9001 Quality Management
                </Link>
              </li>
              <li>
                <Link to="/courses?category=ISO 22000" className="text-sm text-slate-400 hover:text-white transition-colors">
                  ISO 22000 Food Safety
                </Link>
              </li>
              <li>
                <Link to="/courses?category=ISO 27001" className="text-sm text-slate-400 hover:text-white transition-colors">
                  ISO 27001 Information Security
                </Link>
              </li>
              <li>
                <Link to="/courses?category=ISO 31000" className="text-sm text-slate-400 hover:text-white transition-colors">
                  ISO 31000 Risk Management
                </Link>
              </li>
              <li>
                <Link to="/courses?category=FSSC 22000" className="text-sm text-slate-400 hover:text-white transition-colors">
                  FSSC 22000
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">Contact Info</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-400" data-testid="footer-locations">
                  <Link to="/iso-training-consulting-usa" className="hover:text-white transition-colors" data-testid="footer-us-link">Madison, Mississippi, USA</Link><br />
                  Kingston, Jamaica
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-accent flex-shrink-0" />
                <a href="tel:+18768333918" className="text-sm text-slate-400 hover:text-white transition-colors">
                  +1 (876) 833-3918
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-accent flex-shrink-0" />
                <a href="tel:+19548301186" className="text-sm text-slate-400 hover:text-white transition-colors">
                  +1 (954) 830-1186
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-accent flex-shrink-0" />
                <a href="mailto:info@comply-cms.com" className="text-sm text-slate-400 hover:text-white transition-colors">
                  info@comply-cms.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-slate-400">
              © {currentYear} Compliance Management Systems | Authorized PECB Partner
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link to="/admin/login" className="text-sm text-slate-500 hover:text-slate-300 transition-colors">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
